import React, { useState } from 'react';
import { Bell, X, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';

interface PushNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestPermission: () => Promise<void>;
}

export const PushNotificationModal: React.FC<PushNotificationModalProps> = ({
  isOpen,
  onClose,
  onRequestPermission,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestPermission = async () => {
    setIsLoading(true);
    try {
      await onRequestPermission();
      onClose();
    } catch (error) {
      console.error('Failed to request permission:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 animate-fade-in">
      {/* Modal Container */}
      <div className="w-full max-w-sm mx-4 animate-slide-up">
        <Card className="bg-white rounded-2xl shadow-2xl border-0 overflow-hidden">
          {/* Header with Icon */}
          <CardHeader className="bg-gradient-to-br from-primary-blue to-blue-600 text-white text-center pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Bell className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-bold mb-2">Stay Updated!</h2>
            <p className="text-white/90 text-sm">
              Get notified about your orders and exclusive offers
            </p>
          </CardHeader>

          {/* Content */}
          <CardContent className="p-6">
            {/* Benefits List */}
            <div className="space-y-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Order Updates</h3>
                  <p className="text-gray-600 text-xs">
                    Track your orders in real-time with instant notifications
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Exclusive Offers</h3>
                  <p className="text-gray-600 text-xs">
                    Be the first to know about sales and special discounts
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">New Arrivals</h3>
                  <p className="text-gray-600 text-xs">
                    Discover fresh products and trending items
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleRequestPermission}
                disabled={isLoading}
                className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-semibold rounded-xl transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Requesting...</span>
                  </div>
                ) : (
                  'Allow Notifications'
                )}
              </Button>
              
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-xl transition-colors"
              >
                Maybe Later
              </Button>
            </div>

            {/* Privacy Note */}
            <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
              We respect your privacy. You can change notification settings anytime in your profile.
            </p>
          </CardContent>
        </Card>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </div>
  );
}; 